import { PDFDocument } from "pdf-lib";
import { documentService, type DocumentDto } from "../../../services/documentService";
import { GeschuetztesPdfFehler, SeitenFehler, bildAlsPdf, istVerschluesselt, type SeitenQuelle } from "./seitenOps";

/**
 * Macht aus einem KMVS-Dokument oder einer Datei vom PC eine `SeitenQuelle`
 * für den Seiten-Organisator. PDFs gehen direkt durch, PNG/JPG werden zu einer
 * A4-Seite (`bildAlsPdf`), alles andere wird abgelehnt.
 */

/** Für Datei-Wähler und Dokumentauswahl: was sich als Seiten einfügen lässt. */
export const EINFUEGBAR_ACCEPT = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";

export class NichtEinfuegbarFehler extends SeitenFehler {
    constructor(public readonly dateiname: string) {
        super(`„${dateiname}“ ist kein PDF oder Bild.`);
        this.name = "NichtEinfuegbarFehler";
    }
}

type Art = "pdf" | "image/png" | "image/jpeg";

/**
 * Typ am Inhalt erkennen, nicht an Endung oder MIME-Angabe — Dateien aus
 * Mail-Anhängen tragen oft falsche oder gar keine Typen.
 */
function erkenneArt(b: Uint8Array): Art | null {
    // „%PDF“ darf laut Spezifikation nicht ganz vorn stehen (Müll davor kommt vor).
    const kopf = String.fromCharCode(...b.subarray(0, Math.min(b.length, 1024)));
    if (kopf.includes("%PDF")) return "pdf";
    if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return "image/png";
    if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
    return null;
}

async function alsQuelle(id: string, name: string, roh: Uint8Array): Promise<SeitenQuelle> {
    const art = erkenneArt(roh);
    if (!art) throw new NichtEinfuegbarFehler(name);
    const bytes = art === "pdf" ? roh : await bildAlsPdf(roh, art);
    let seitenzahl: number;
    try {
        seitenzahl = (await PDFDocument.load(bytes.slice(), { updateMetadata: false })).getPageCount();
    } catch (e) {
        if (istVerschluesselt(e)) throw new GeschuetztesPdfFehler(name);
        throw e;
    }
    return { id, name, bytes, seitenzahl };
}

// Mehrere gleichnamige Dateien in derselben Millisekunde brauchen trotzdem eigene Ids.
let dateiZaehler = 0;

export async function quelleAusDatei(datei: File): Promise<SeitenQuelle> {
    const roh = new Uint8Array(await datei.arrayBuffer());
    return alsQuelle(`datei:${datei.name}:${Date.now()}:${++dateiZaehler}`, datei.name, roh);
}

export async function quelleAusDokument(
    doc: Pick<DocumentDto, "id" | "fileName" | "mimeType">,
    ladeBlob: (id: number) => Promise<Blob> = (id) => documentService.fetchBlob(id),
): Promise<SeitenQuelle> {
    const roh = new Uint8Array(await (await ladeBlob(doc.id)).arrayBuffer());
    return alsQuelle(`doc:${doc.id}`, doc.fileName, roh);
}
